import React, { useState } from 'react';

import { Trans, useLingui } from '@lingui/react/macro';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Box,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import classNames from 'classnames';
import { useHistory } from 'react-router';

import { useIsEmbedded } from '@/utils/hooks';

import { FRENCH_LOCALES } from '../../../locales/helpers';
import routes from '../../../routes/routes.constants';
import { colors } from '../../../utils/theme';
import { PracticeFeatureFlagKey, useFeatureFlag } from '../../../utils/user';

const useStyles = makeStyles(() => ({
  container: {
    background: colors.OFF_WHITE,
    position: 'sticky',
  },
  menuCaption: {
    fontFamily: 'proxima-nova, sans-serif',
  },
  backIcon: {
    color: colors.PRIMARY,
    fontSize: '20px',
    verticalAlign: 'middle',
    marginLeft: '24px',
    marginRight: '10px',
  },
  backButton: {
    display: 'inline-block',
    textAlign: 'left',
    verticalAlign: 'middle',
    width: '100%',
    paddingBottom: '16px',
    paddingTop: '16px',
    marginBottom: '8px',
    marginTop: '32px',
  },
  listItems: {
    paddingLeft: '50px',
    fontSize: '14px',
    '&:focus': {
      background: colors.VERY_LIGHT_GREY,
    },
  },
  padding: {
    paddingTop: '16px',
    paddingLeft: '30px',
  },
  selected: {
    background: colors.VERY_LIGHT_GREY,
  },
}));

interface Props {
  pdfName: string;
}

const useComputeAboutMenuOptions = (isVDAEnabled: boolean) => {
  const { i18n, t } = useLingui();
  if (FRENCH_LOCALES.includes(i18n.locale)) {
    return isVDAEnabled
      ? {
          [t`Videa Dental AI`]: 'VDA_ABOUT',
          [t`Videa Perio Assist`]: 'VPA_ABOUT',
        }
      : {
          [t`Videa Perio Assist`]: 'VPA_ABOUT',
        };
  }
  return isVDAEnabled
    ? {
        [t`Videa Dental AI`]: 'VDA_ABOUT',
        [t`Videa Perio Assist`]: 'VPA_ABOUT',
      }
    : {
        [t`Videa Perio Assist`]: 'VPA_ABOUT',
      };
};

const useComputeIfuMenuOptions = (isVDAEnabled: boolean) => {
  const { i18n, t } = useLingui();
  if (FRENCH_LOCALES.includes(i18n.locale)) {
    return isVDAEnabled
      ? {
          [t`Videa Dental AI`]: 'VDA_IFU',
          [t`Videa Perio Assist`]: 'VPA_IFU',
          [t`Videa Segmentation and Patient View`]: 'SEG_IFU',
          [t`Videa Decision Support Software`]: 'DSS_IFU',
        }
      : {
          [t`Videa Perio Assist`]: 'VPA_IFU',
          [t`Videa Segmentation and Patient View`]: 'SEG_IFU',
          [t`Videa Decision Support Software`]: 'DSS_IFU',
        };
  }
  return isVDAEnabled
    ? {
        [t`Videa Dental AI`]: 'VDA_IFU',
        [t`Videa Perio Assist`]: 'VPA_IFU',
        [t`Videa Segmentation and Patient View`]: 'SEG_IFU',
        [t`Videa Decision Support Software`]: 'DSS_IFU',
      }
    : {
        [t`Videa Perio Assist`]: 'VPA_IFU',
        [t`Videa Segmentation and Patient View`]: 'SEG_IFU',
        [t`Videa Decision Support Software`]: 'DSS_IFU',
      };
};

const AboutSidebar: React.FC<Props> = ({ pdfName }) => {
  const classes = useStyles();
  const history = useHistory();

  const [, setIsDefaultDisplay] = useState(true);

  const isVDAEnabled = !!useFeatureFlag(
    PracticeFeatureFlagKey.VIDEA_DENTAL_ASSIST_ENABLED,
  )?.value;

  const aboutMenuOptions = useComputeAboutMenuOptions(isVDAEnabled);
  const ifuMenuOptions = useComputeIfuMenuOptions(isVDAEnabled);
  const isEmbedded = useIsEmbedded();

  const handleTabClick = (pdf: string) => {
    setIsDefaultDisplay(false);
    const aboutRoute = isEmbedded
      ? `/embedded${routes.ABOUT}/${pdf}`
      : `${routes.ABOUT}/${pdf}`;

    history.push(aboutRoute);
  };

  const navigateBack = () => {
    if (isEmbedded && history.length > 1) {
      history.goBack();
    } else if (!isEmbedded) {
      history.push(routes.UPLOADER);
    }
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      height={1}
      className={classes.container}
      overflow="auto"
    >
      {(!isEmbedded || history.length > 1) && (
        <Button
          onClick={() => {
            navigateBack();
          }}
          className={classes.backButton}
        >
          <ArrowBackIcon className={classes.backIcon} />
          <span
            data-testid="go-back-button"
            className={classNames(
              classes.menuCaption,
              'vai-font-semibold vai-capitalize',
            )}
          >
            <Trans>Go Back</Trans>
          </span>
        </Button>
      )}
      <Box display="flex" flexDirection="column" width={1}>
        <span className={`${classes.menuCaption} ${classes.padding}`}>
          <Trans>About</Trans>
        </span>

        <List>
          {Object.entries(aboutMenuOptions).map(([text, name]) => (
            <ListItem
              button
              key={text}
              data-testid={name}
              className={
                pdfName === name
                  ? `${classes.listItems} ${classes.selected}`
                  : classes.listItems
              }
              onClick={() => {
                handleTabClick(name);
              }}
            >
              <ListItemText primary={text} />
            </ListItem>
          ))}
        </List>
      </Box>
      <Box display="flex" flexDirection="column" width={1} paddingBottom={2}>
        <span className={`${classes.menuCaption} ${classes.padding}`}>
          <Trans>IFU</Trans>
        </span>

        <List>
          {Object.entries(ifuMenuOptions).map(([text, name]) => (
            <ListItem
              button
              key={text}
              data-testid={name}
              className={
                pdfName === name
                  ? `${classes.listItems} ${classes.selected}`
                  : classes.listItems
              }
              onClick={() => {
                handleTabClick(name);
              }}
            >
              <ListItemText primary={text} />
            </ListItem>
          ))}
        </List>
      </Box>
      <Divider variant="middle" />
    </Box>
  );
};
export default AboutSidebar;
